import {
  animate,
  AnimationEvent,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBriefcase,
  faEnvelopeOpenText,
  faFileLines,
  faGear,
  faHome,
  faSearch,
  faUser,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  CommandComponent,
  EmptyDirective,
  GroupComponent,
  InputDirective,
  ItemDirective,
  ListComponent,
} from '@ngxpert/cmdk';
import { fromEvent } from 'rxjs';
import { transitionString } from '../../../constants/transition';
import {
  PlatformApplication,
  PlatformBoardJob,
  PlatformInvitation,
  PlatformSavedJob,
} from '../../models/platform.models';
import { PlatformCmdkService } from '../../services/platform-cmdk.service';
import { PlatformJobsService } from '../../services/platform-jobs.service';
import { PlatformKbdComponent } from '../kbd/platform-kbd.component';

const RESULT_LIMIT = 8;

type StaticCmdkItem = {
  key: string;
  icon: IconDefinition;
  keywords?: readonly string[];
  action: () => void;
};

type CmdkJobResult = {
  id: number;
  title: string;
  companyName: string;
  location: string;
};

type CmdkApplicationResult = {
  id: number;
  title: string;
  companyName: string;
  kind: 'application' | 'invitation';
  jobId: number | null;
};

@Component({
  selector: 'app-platform-cmdk',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PlatformKbdComponent,
    FontAwesomeModule,
    TranslocoPipe,
    CommandComponent,
    InputDirective,
    ListComponent,
    GroupComponent,
    ItemDirective,
    EmptyDirective,
  ],
  templateUrl: './platform-cmdk.component.html',
  styleUrl: './platform-cmdk.component.scss',
  animations: [
    trigger('backdrop', [
      state('closed', style({ opacity: 0 })),
      state('open', style({ opacity: 1 })),
      transition('closed => open', animate(transitionString)),
      transition('open => closed', animate(transitionString)),
    ]),
  ],
})
export class PlatformCmdkComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly cmdkService = inject(PlatformCmdkService);
  private readonly jobs = inject(PlatformJobsService);
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);

  readonly icons = {
    search: faSearch,
    job: faBriefcase,
    application: faFileLines,
    invitation: faEnvelopeOpenText,
  };

  protected readonly open = this.cmdkService.open;
  protected readonly isMac = signal(false);
  protected readonly query = signal('');
  private readonly pendingQueryReset = signal(false);

  protected readonly cmdkFilter = (_value: string, _search: string): boolean => true;

  protected readonly queryTrimmed = computed(() => this.query().trim());
  protected readonly showEmptyCategoryHint = computed(
    () => this.queryTrimmed().length > 0
  );

  protected readonly staticJobs = (): StaticCmdkItem[] => [
    {
      key: 'jobboard.platform.cmdk.actions.open-jobs',
      icon: faBriefcase,
      keywords: ['jobs', 'stellen', 'empfehlungen', 'recommendations'],
      action: () => this.router.navigate(['/jobs']),
    },
  ];

  protected readonly staticApplications = (): StaticCmdkItem[] => [
    {
      key: 'jobboard.platform.cmdk.actions.open-applications',
      icon: faFileLines,
      keywords: ['bewerbungen', 'applications', 'tracking'],
      action: () => this.router.navigate(['/applications']),
    },
  ];

  protected readonly staticFunctions = (): StaticCmdkItem[] => [
    {
      key: 'jobboard.platform.cmdk.actions.open-dashboard',
      icon: faHome,
      keywords: ['dashboard', 'übersicht', 'home'],
      action: () => this.router.navigate(['/dashboard']),
    },
    {
      key: 'jobboard.platform.cmdk.actions.open-profile',
      icon: faUser,
      keywords: ['profil', 'profile', 'cv', 'lebenslauf'],
      action: () => this.router.navigate(['/profile']),
    },
    {
      key: 'jobboard.platform.cmdk.actions.open-settings',
      icon: faGear,
      keywords: ['einstellungen', 'settings', 'konto'],
      action: () => this.router.navigate(['/settings']),
    },
    {
      key: 'jobboard.platform.cmdk.actions.open-jobboard',
      icon: faBriefcase,
      keywords: ['jobboard', 'öffentliches board'],
      action: () => this.router.navigate(['/']),
    },
  ];

  protected readonly filteredStaticJobs = computed(() =>
    this.filterStaticItems(this.staticJobs(), this.queryTrimmed())
  );
  protected readonly filteredStaticApplications = computed(() =>
    this.filterStaticItems(this.staticApplications(), this.queryTrimmed())
  );
  protected readonly filteredStaticFunctions = computed(() =>
    this.filterStaticItems(this.staticFunctions(), this.queryTrimmed())
  );

  protected readonly jobResults = computed(() =>
    this.searchJobs(this.queryTrimmed())
  );
  protected readonly applicationResults = computed(() =>
    this.searchApplications(this.queryTrimmed())
  );

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  constructor() {
    this.isMac.set(
      (this.document.defaultView?.navigator.platform ?? '').toLowerCase().includes('mac')
    );

    fromEvent<KeyboardEvent>(this.document, 'keydown')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.onKeydown(event));

    effect(() => {
      if (!this.open()) {
        return;
      }
      queueMicrotask(() => {
        this.searchInput?.nativeElement?.focus();
      });
    });

    effect(() => {
      if (this.open()) {
        return;
      }
      this.pendingQueryReset.set(true);
    });
  }

  /**
   * Handles global keyboard shortcuts for the command palette.
   * @param event - Keyboard event
   */
  protected onKeydown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();
    const isK = key === 'k';
    const isEsc = key === 'escape';
    const hasCmdOrCtrl = this.isMac() ? event.metaKey : event.ctrlKey;

    if (isK && hasCmdOrCtrl) {
      event.preventDefault();
      event.stopPropagation();
      this.cmdkService.toggleCmdk();
      return;
    }

    if (isEsc && this.open()) {
      event.preventDefault();
      event.stopPropagation();
      this.cmdkService.closeCmdk();
    }
  }

  /**
   * Closes the palette when clicking the backdrop.
   * @param event - Mouse event
   */
  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.cmdkService.closeCmdk();
    }
  }

  /**
   * Updates the search query from the input field.
   * @param event - Input event
   */
  protected onQueryInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.query.set(value);
  }

  /**
   * Resets query state after the close animation finishes.
   * @param event - Animation event
   */
  protected onBackdropAnimationDone(event: AnimationEvent): void {
    if (event.toState !== 'closed' || !this.pendingQueryReset()) {
      return;
    }

    this.pendingQueryReset.set(false);
    this.query.set('');

    const input = this.searchInput?.nativeElement;
    if (input) {
      input.value = '';
    }
  }

  /**
   * Executes a static command palette action.
   * @param item - Selected command item
   * @param event - Optional selection event
   */
  protected onStaticAction(item: StaticCmdkItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    item.action();
    this.closeAndReset();
  }

  /**
   * Opens a matched job from the palette.
   * @param item - Job result
   * @param event - Optional selection event
   */
  protected onOpenJob(item: CmdkJobResult, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.router.navigate(['/jobs', 'recommendations', item.id]);
    this.closeAndReset();
  }

  /**
   * Opens a matched application or invitation from the palette.
   * @param item - Application result
   * @param event - Optional selection event
   */
  protected onOpenApplication(item: CmdkApplicationResult, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (item.kind === 'invitation' && item.jobId != null) {
      this.router.navigate(['/jobs', 'recommendations', item.jobId]);
    } else {
      this.router.navigate(['/applications']);
    }
    this.closeAndReset();
  }

  /**
   * Closes the palette and clears the query.
   */
  private closeAndReset(): void {
    this.cmdkService.closeCmdk();
    this.query.set('');
  }

  /**
   * Filters static palette items by translated label and keywords.
   * @param items - Static command items
   * @param query - Search query
   * @returns Filtered items
   */
  private filterStaticItems(items: StaticCmdkItem[], query: string): StaticCmdkItem[] {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return items;
    }

    return items.filter((item) => {
      const label = this.translocoService.translate(item.key);
      if ((label ?? '').toLowerCase().includes(normalizedQuery)) {
        return true;
      }
      return (item.keywords ?? []).some((keyword) =>
        keyword.toLowerCase().includes(normalizedQuery)
      );
    });
  }

  /**
   * Searches listed, recommended and saved jobs by title, company or location.
   * @param query - Search query
   * @returns Matching job results
   */
  private searchJobs(query: string): CmdkJobResult[] {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return [];
    }

    const byId = new Map<number, CmdkJobResult>();

    const addBoardJob = (job: PlatformBoardJob): void => {
      if (byId.has(job.id) || !this.matchesJobQuery(job, normalizedQuery)) {
        return;
      }
      byId.set(job.id, {
        id: job.id,
        title: job.jobTitle,
        companyName: job.companyName,
        location: job.location,
      });
    };

    for (const job of this.jobs.listedJobs()) {
      addBoardJob(job);
    }
    for (const job of this.jobs.recommendedJobs()) {
      addBoardJob(job);
    }
    for (const saved of this.jobs.savedJobs()) {
      if (byId.has(saved.jobId) || !this.matchesSavedJobQuery(saved, normalizedQuery)) {
        continue;
      }
      byId.set(saved.jobId, {
        id: saved.jobId,
        title: saved.jobTitle,
        companyName: saved.companyName,
        location: saved.location,
      });
    }

    return [...byId.values()].slice(0, RESULT_LIMIT);
  }

  /**
   * Searches applications and invitations by job or company.
   * @param query - Search query
   * @returns Matching application results
   */
  private searchApplications(query: string): CmdkApplicationResult[] {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return [];
    }

    const results: CmdkApplicationResult[] = [];

    for (const application of this.jobs.applications()) {
      if (!this.matchesApplicationQuery(application, normalizedQuery)) {
        continue;
      }
      results.push({
        id: application.id,
        title: application.jobTitle,
        companyName: application.companyName,
        kind: 'application',
        jobId: null,
      });
      if (results.length >= RESULT_LIMIT) {
        return results;
      }
    }

    for (const invitation of this.jobs.invitations()) {
      if (!this.matchesInvitationQuery(invitation, normalizedQuery)) {
        continue;
      }
      results.push({
        id: invitation.id,
        title: invitation.jobTitle,
        companyName: invitation.companyName,
        kind: 'invitation',
        jobId: invitation.jobId,
      });
      if (results.length >= RESULT_LIMIT) {
        break;
      }
    }

    return results;
  }

  /**
   * @param job - Board job
   * @param query - Normalized query
   * @returns Whether the job matches
   */
  private matchesJobQuery(job: PlatformBoardJob, query: string): boolean {
    return this.includesQuery(
      [job.jobTitle, job.companyName, job.location],
      query
    );
  }

  /**
   * @param job - Saved job
   * @param query - Normalized query
   * @returns Whether the saved job matches
   */
  private matchesSavedJobQuery(job: PlatformSavedJob, query: string): boolean {
    return this.includesQuery(
      [job.jobTitle, job.companyName, job.location],
      query
    );
  }

  /**
   * @param application - Tracked application
   * @param query - Normalized query
   * @returns Whether the application matches
   */
  private matchesApplicationQuery(
    application: PlatformApplication,
    query: string
  ): boolean {
    return this.includesQuery(
      [application.jobTitle, application.companyName, application.location],
      query
    );
  }

  /**
   * @param invitation - Invitation
   * @param query - Normalized query
   * @returns Whether the invitation matches
   */
  private matchesInvitationQuery(
    invitation: PlatformInvitation,
    query: string
  ): boolean {
    return this.includesQuery(
      [invitation.jobTitle, invitation.companyName, invitation.location],
      query
    );
  }

  /**
   * @param values - Candidate strings
   * @param query - Normalized query
   * @returns Whether any value contains the query
   */
  private includesQuery(values: Array<string | null | undefined>, query: string): boolean {
    return values.some((value) => (value ?? '').toLowerCase().includes(query));
  }
}
